// src/types.ts
var QUALITY_CODES = {
  CANVAS_UNSPECIFIED: "W_QUALITY_CANVAS_UNSPECIFIED",
  CANVAS_NONSTANDARD: "W_QUALITY_CANVAS_NONSTANDARD",
  CANVAS_LEGACY: "W_QUALITY_CANVAS_LEGACY",
  TEXT_OVERFLOW: "W_QUALITY_TEXT_OVERFLOW",
  TEXT_TIGHT: "W_QUALITY_TEXT_TIGHT",
  SLIDE_DENSITY: "W_QUALITY_SLIDE_DENSITY",
  FONT_SIZE_MIN: "W_QUALITY_FONT_SIZE_MIN",
  TABLE_WIDTH_OVERFLOW: "W_QUALITY_TABLE_WIDTH_OVERFLOW",
  FRAME_COLLISION: "W_QUALITY_FRAME_COLLISION",
  HEADING_SKIP: "W_QUALITY_HEADING_SKIP",
  TEXT_CONTRAST: "W_QUALITY_TEXT_CONTRAST",
  SVG_TEXT_CLIPPED: "W_QUALITY_SVG_TEXT_CLIPPED",
  LINE_BOX_COLLAPSE: "W_QUALITY_LINE_BOX_COLLAPSE",
  PLACEHOLDER_TEXT: "W_QUALITY_PLACEHOLDER_TEXT",
  SCAFFOLD_MARKER: "W_QUALITY_SCAFFOLD_MARKER",
  BOX_OVERLAP: "W_QUALITY_BOX_OVERLAP",
  FONT_COUNT: "W_QUALITY_FONT_COUNT",
  OFF_PALETTE: "W_QUALITY_OFF_PALETTE",
  CHART_3D: "W_QUALITY_CHART_3D",
  CHART_OVERLOADED: "W_QUALITY_CHART_OVERLOADED",
  CHART_AXIS_BASELINE: "W_QUALITY_CHART_AXIS_BASELINE",
  CHART_SERIES_COLORS: "W_QUALITY_CHART_SERIES_COLORS",
  CHART_UNITS: "W_QUALITY_CHART_UNITS",
  CHART_ANNOTATION: "W_QUALITY_CHART_ANNOTATION",
  TABLE_NUMERIC_ALIGN: "W_QUALITY_TABLE_NUMERIC_ALIGN",
  TABLE_MIXED_DECIMALS: "W_QUALITY_TABLE_MIXED_DECIMALS",
  TABLE_GRID: "W_QUALITY_TABLE_GRID",
  TABLE_ROW_COUNT: "W_QUALITY_TABLE_ROW_COUNT",
  SLOT_BUDGET: "W_QUALITY_SLOT_BUDGET",
  OFF_CANVAS: "W_QUALITY_OFF_CANVAS",
  CHROME_MISSING: "W_QUALITY_CHROME_MISSING",
  EXHIBIT_MISSING: "W_QUALITY_EXHIBIT_MISSING",
  ACTION_TITLE_LENGTH: "W_QUALITY_ACTION_TITLE_LENGTH",
  TYPE_OFF_SCALE: "W_QUALITY_TYPE_OFF_SCALE",
  TYPE_SIZE_COUNT: "W_QUALITY_TYPE_SIZE_COUNT",
  TYPE_ROLE_DRIFT: "W_QUALITY_TYPE_ROLE_DRIFT",
  TITLE_DRIFT: "W_QUALITY_TITLE_DRIFT",
  SECTION_EMPTY: "W_QUALITY_SECTION_EMPTY",
  SECTION_UNTITLED: "W_QUALITY_SECTION_UNTITLED",
  SLIDE_UNTITLED: "W_QUALITY_SLIDE_UNTITLED",
  BODY_MEASURE: "W_QUALITY_BODY_MEASURE",
  HEADING_ORPHAN: "W_QUALITY_HEADING_ORPHAN",
  FIGURE_UNLABELLED: "W_QUALITY_FIGURE_UNLABELLED",
  CONTENTS_MISSING: "W_QUALITY_CONTENTS_MISSING",
  IMAGE_ASPECT: "W_QUALITY_IMAGE_ASPECT",
  BULLET_COUNT: "W_QUALITY_BULLET_COUNT",
  BULLET_LENGTH: "W_QUALITY_BULLET_LENGTH",
  SAFE_AREA: "W_QUALITY_SAFE_AREA",
  RENDERED_CLIP: "W_QUALITY_RENDERED_CLIP",
  RENDERED_SPILL: "W_QUALITY_RENDERED_SPILL",
  RENDERED_OVERLAP: "W_QUALITY_RENDERED_OVERLAP",
  RENDERED_TEXT_MISSING: "W_QUALITY_RENDERED_TEXT_MISSING",
  RENDERED_PLACEHOLDER: "W_QUALITY_RENDERED_PLACEHOLDER",
  RENDERED_FONT_SUBSTITUTED: "W_QUALITY_RENDERED_FONT_SUBSTITUTED",
  RENDERED_EMPTY_PAGE: "W_QUALITY_RENDERED_EMPTY_PAGE",
  RENDERED_HEADING_STRANDED: "W_QUALITY_RENDERED_HEADING_STRANDED",
  RENDERED_PARAGRAPH_SPLIT: "W_QUALITY_RENDERED_PARAGRAPH_SPLIT",
  RENDERED_PAGE_UNDERFILLED: "W_QUALITY_RENDERED_PAGE_UNDERFILLED",
  RENDERED_TABLE_SPLIT: "W_QUALITY_RENDERED_TABLE_SPLIT"
};

// src/policy.ts
var SEVERITY_RANK = {
  error: 0,
  warning: 1,
  info: 2
};
var QualityPolicyError = class extends Error {
  code = "QUALITY_POLICY_INVALID";
  constructor(message) {
    super(message);
    this.name = "QualityPolicyError";
  }
};
function isSeverity(value) {
  return value === "error" || value === "warning" || value === "info";
}
function assertRuleConfigurations(rules, origin) {
  for (const [ruleId, configuration] of Object.entries(rules ?? {})) {
    if (typeof configuration !== "object" || configuration === null || Array.isArray(configuration)) {
      throw new QualityPolicyError(
        `Quality ${origin} rule "${ruleId}" has invalid configuration ${JSON.stringify(configuration)}; expected an object`
      );
    }
    if (configuration.severity !== void 0 && !isSeverity(configuration.severity)) {
      throw new QualityPolicyError(
        `Quality ${origin} rule "${ruleId}" has invalid severity ${JSON.stringify(configuration.severity)}; expected "error", "warning" or "info"`
      );
    }
  }
}
function assertValidQualityPolicy(policy) {
  if (!policy) return;
  if (policy.gate !== void 0 && policy.gate !== "none" && !isSeverity(policy.gate)) {
    throw new QualityPolicyError(
      `Quality policy has invalid gate ${JSON.stringify(policy.gate)}; expected "none", "error", "warning" or "info"`
    );
  }
  assertRuleConfigurations(policy.rules, "policy");
  if (policy.suppressions !== void 0) {
    if (!Array.isArray(policy.suppressions)) {
      throw new QualityPolicyError(
        `Quality policy has invalid suppressions ${JSON.stringify(policy.suppressions)}; expected an array`
      );
    }
    policy.suppressions.forEach((suppression, index) => {
      if (typeof suppression !== "object" || suppression === null || Array.isArray(suppression)) {
        throw new QualityPolicyError(
          `Quality policy suppression ${index} is ${JSON.stringify(suppression)}; expected an object`
        );
      }
      for (const key of ["ruleId", "code", "path", "reason"]) {
        if (suppression[key] !== void 0 && typeof suppression[key] !== "string") {
          throw new QualityPolicyError(
            `Quality policy suppression ${index} has invalid ${key} ${JSON.stringify(suppression[key])}; expected a string`
          );
        }
      }
      if (suppression.pathMatch !== void 0 && suppression.pathMatch !== "exact" && suppression.pathMatch !== "subtree") {
        throw new QualityPolicyError(
          `Quality policy suppression ${index} has invalid pathMatch ${JSON.stringify(suppression.pathMatch)}; expected "exact" or "subtree"`
        );
      }
    });
  }
  if (policy.maxDiagnostics !== void 0 && (!Number.isInteger(policy.maxDiagnostics) || policy.maxDiagnostics < 0)) {
    throw new QualityPolicyError(
      `Quality policy has invalid maxDiagnostics ${JSON.stringify(policy.maxDiagnostics)}; expected a non-negative integer`
    );
  }
  if (policy.onRuleError !== void 0 && policy.onRuleError !== "continue" && policy.onRuleError !== "throw") {
    throw new QualityPolicyError(
      `Quality policy has invalid onRuleError ${JSON.stringify(policy.onRuleError)}; expected "continue" or "throw"`
    );
  }
}
function assertValidQualityProfile(profile) {
  if (!profile) return;
  assertRuleConfigurations(profile.rules, "profile");
}
function mergeQualityProfiles(base, override) {
  const rules = { ...base.rules };
  for (const [ruleId, configuration] of Object.entries(override.rules ?? {})) {
    const shipped = rules[ruleId];
    rules[ruleId] = shipped ? {
      ...shipped,
      ...configuration,
      parameters: {
        ...shipped.parameters,
        ...configuration.parameters
      }
    } : configuration;
  }
  return {
    ...base,
    ...override,
    rules,
    parameters: { ...base.parameters, ...override.parameters }
  };
}
function resolveRuleConfiguration(rule, profile, policy) {
  const profileRule = profile?.rules?.[rule.id];
  const policyRule = policy?.rules?.[rule.id];
  const layers = [
    ["rule", rule.defaultParameters],
    ["profile", profile?.parameters],
    ["profile", profileRule?.parameters],
    ["policy", policyRule?.parameters]
  ];
  const parameters = {};
  const parameterSources = {};
  for (const [source, values] of layers)
    for (const [name, value] of Object.entries(values ?? {})) {
      parameters[name] = value;
      parameterSources[name] = source;
    }
  return {
    enabled: policyRule?.enabled ?? profileRule?.enabled ?? rule.defaultEnabled ?? true,
    enabledSource: policyRule?.enabled !== void 0 ? "policy" : profileRule?.enabled !== void 0 ? "profile" : "rule",
    severity: policyRule?.severity ?? profileRule?.severity ?? rule.defaultSeverity,
    severityOverride: policyRule?.severity ?? profileRule?.severity,
    parameters,
    parameterSources
  };
}
var SOURCE_RANK = {
  rule: 0,
  profile: 1,
  policy: 2
};
function configurationSource(configuration, ...parameters) {
  return parameters.map((name) => configuration.parameterSources[name] ?? "rule").reduce(
    (latest, source) => SOURCE_RANK[source] > SOURCE_RANK[latest] ? source : latest,
    configuration.enabledSource
  );
}
function configurationLabel(source, profile) {
  if (source === "policy") return "the quality policy";
  if (source === "profile") return `the ${profile?.id ?? "selected"} profile`;
  return "the rule default";
}
function matchesPath(diagnosticPath, suppression) {
  if (suppression.path === void 0) return true;
  if (suppression.pathMatch !== "subtree") {
    return diagnosticPath === suppression.path;
  }
  if (suppression.path === "") return true;
  return diagnosticPath === suppression.path || diagnosticPath.startsWith(`${suppression.path}/`);
}
function isSuppressed(diagnostic, suppressions = []) {
  return suppressions.some((suppression) => {
    const hasSelector = suppression.ruleId !== void 0 || suppression.code !== void 0 || suppression.path !== void 0;
    if (!hasSelector) return false;
    if (suppression.ruleId !== void 0 && diagnostic.ruleId !== suppression.ruleId) {
      return false;
    }
    if (suppression.code !== void 0 && diagnostic.code !== suppression.code) {
      return false;
    }
    return matchesPath(diagnostic.path, suppression);
  });
}
function isBlocking(severity, gate = "none") {
  if (gate === "none") return false;
  return SEVERITY_RANK[severity] <= SEVERITY_RANK[gate];
}
function severityRank(severity) {
  return SEVERITY_RANK[severity];
}

// src/brand.ts
var COLOR_KEY = /(colou?r|fill|stroke|shade|tint|background|border)/i;
var FAMILY_KEYS = /* @__PURE__ */ new Set(["family", "fontFace", "fontFamily"]);
var HEX = /^#?([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
function pointerSegment(value) {
  return value.replace(/~/g, "~0").replace(/\//g, "~1");
}
function normalizeHex(value) {
  const found = HEX.exec(value.trim());
  if (!found) return void 0;
  const digits = found[1];
  const full = digits.length === 3 ? digits[0] + digits[0] + digits[1] + digits[1] + digits[2] + digits[2] : digits;
  return `#${full.toUpperCase()}`;
}
function walk(node, path, key, options) {
  if (typeof node === "string") {
    options.onString(node, path, key);
    return;
  }
  if (Array.isArray(node)) {
    node.forEach(
      (entry, index) => walk(entry, `${path}/${index}`, key, options)
    );
    return;
  }
  if (typeof node !== "object" || node === null) return;
  const record2 = node;
  if (record2.enabled === false) return;
  for (const [name, value] of Object.entries(record2)) {
    walk(value, `${path}/${pointerSegment(name)}`, name, options);
  }
}
function collectColorLiterals(document) {
  const found = [];
  walk(document, "", "", {
    onString: (value, path, key) => {
      if (!COLOR_KEY.test(key)) return;
      const hex = normalizeHex(value);
      if (hex) found.push({ path, raw: value, hex });
    }
  });
  return found;
}
function collectFontFamilies(document) {
  const found = [];
  walk(document, "", "", {
    onString: (value, path, key) => {
      const family = value.trim();
      if (FAMILY_KEYS.has(key) && family !== "") found.push({ path, family });
    }
  });
  return found;
}
function nearestPaletteToken(hex, palette) {
  const target = normalizeHex(hex);
  if (!target) return void 0;
  const [tr, tg, tb] = channels(target);
  let best;
  for (const token of Object.keys(palette).sort()) {
    const candidate = normalizeHex(palette[token] ?? "");
    if (!candidate) continue;
    const [cr, cg, cb] = channels(candidate);
    const meanRed = (tr + cr) / 2;
    const dr = tr - cr;
    const dg = tg - cg;
    const db = tb - cb;
    const distance = Math.sqrt(
      (2 + meanRed / 256) * dr * dr + 4 * dg * dg + (2 + (255 - meanRed) / 256) * db * db
    );
    if (!best || distance < best.distance) {
      best = { token, hex: candidate, distance };
    }
  }
  return best;
}
function channels(hex) {
  const value = parseInt(hex.slice(1), 16);
  return [value >> 16 & 255, value >> 8 & 255, value & 255];
}
function offPaletteFinding(input) {
  const { nearest } = input;
  return {
    code: QUALITY_CODES.OFF_PALETTE,
    severity: "info",
    category: "brand",
    certainty: "deterministic",
    message: nearest ? `${input.raw} is not in the theme palette; the nearest token is \`${nearest.token}\` (${nearest.hex}).` : `${input.raw} is not in the theme palette.`,
    path: input.path,
    suggestion: nearest ? `Use "${nearest.token}" so the colour follows the theme, or keep the literal if it is deliberately off-palette.` : "Name a theme colour instead of a literal, so a theme swap repaints it.",
    context: {
      color: input.hex,
      ...nearest && {
        nearestToken: nearest.token,
        nearestHex: nearest.hex,
        distance: Math.round(nearest.distance * 10) / 10
      }
    },
    evidence: {
      actual: input.hex,
      ...nearest && { expected: nearest.hex }
    },
    // `replace`, not `add`. The two are the same on an object member, and
    // very different on an array element: RFC 6902 `add` at `/colors/0`
    // SPLICES, so a fix for one entry of a chart's palette would insert the
    // token and leave the off-palette hex behind at index 1 — a fix that does
    // not make its own finding disappear. The value always exists here, since
    // it was read from this exact pointer, so `replace` is always legal.
    ...nearest && {
      fixes: [
        { op: "replace", path: input.path, value: nearest.token }
      ]
    }
  };
}
function fontCountFinding(input) {
  return {
    code: QUALITY_CODES.FONT_COUNT,
    severity: "warning",
    category: "brand",
    certainty: "deterministic",
    message: `${input.families.length} font families in one document (${input.families.join(", ")}) \u2014 past ${input.maximum} a document reads as assembled rather than designed.`,
    path: input.path,
    suggestion: "Carry the extra weight with size, weight and colour instead of another family; a family the theme already defines costs nothing.",
    context: { families: [...input.families], maximum: input.maximum },
    evidence: {
      actual: input.families.length,
      expected: input.maximum,
      unit: "families"
    },
    ...input.relatedPaths && input.relatedPaths.length > 0 && { relatedPaths: input.relatedPaths }
  };
}

// src/information-design.ts
var DEFAULT_MAXIMUM_CHART_SERIES = 4;
var DEFAULT_MAXIMUM_PIE_SLICES = 6;
function chartInfoDesignFindings(chart, options = {}) {
  const maximumSeries = options.maximumSeries ?? DEFAULT_MAXIMUM_CHART_SERIES;
  const maximumSlices = options.maximumSlices ?? DEFAULT_MAXIMUM_PIE_SLICES;
  const findings = [];
  if (chart.threeD) {
    findings.push({
      code: QUALITY_CODES.CHART_3D,
      severity: "warning",
      category: "information-design",
      certainty: "deterministic",
      message: `\`${chart.chartType}\` draws the data in perspective, which makes the front of the chart read larger than the back \u2014 the comparison the chart exists for is the thing the projection distorts.`,
      path: chart.path,
      suggestion: "Use the flat form of the same chart; depth adds no dimension of data.",
      context: { chartType: chart.chartType },
      evidence: { actual: chart.chartType, summary: "3D projection" }
    });
  }
  if (chart.encoding === "angle") {
    if (chart.categoryCount > maximumSlices) {
      findings.push(
        overloadedFinding(chart, {
          actual: chart.categoryCount,
          maximum: maximumSlices,
          unit: "slices",
          message: `${chart.categoryCount} slices in one ${chart.chartType} \u2014 past ${maximumSlices} the wedges are too close in angle to rank by eye.`,
          suggestion: 'Keep the largest slices and gather the rest into one "other", or use a bar chart, where length ranks at a glance.'
        })
      );
    }
  } else if (chart.seriesCount > maximumSeries) {
    findings.push(
      overloadedFinding(chart, {
        actual: chart.seriesCount,
        maximum: maximumSeries,
        unit: "series",
        message: `${chart.seriesCount} series on one chart \u2014 past ${maximumSeries} the reader spends the chart on the legend.`,
        suggestion: "Show the series that carry the message and drop or aggregate the rest, or split the chart into small multiples."
      })
    );
  }
  if (chart.encoding === "length" && chart.valueAxisMin !== void 0 && chart.valueAxisMin !== 0) {
    findings.push({
      code: QUALITY_CODES.CHART_AXIS_BASELINE,
      severity: "warning",
      category: "information-design",
      certainty: "deterministic",
      message: `The value axis starts at ${chart.valueAxisMin}, so the bars are lengths of a number nobody stated \u2014 a 2% difference can be drawn as a doubling.`,
      path: chart.path,
      suggestion: "Start the value axis at zero. To show a small movement, plot the change itself, or use a line chart, where position rather than length carries the value.",
      context: { valueAxisMin: chart.valueAxisMin },
      evidence: { actual: chart.valueAxisMin, expected: 0 }
    });
  }
  if (!chart.seriesColorsStated) {
    findings.push({
      code: QUALITY_CODES.CHART_SERIES_COLORS,
      severity: "warning",
      category: "brand",
      certainty: "deterministic",
      message: "No series colours: the chart paints in the renderer\u2019s default palette, which belongs to no document.",
      path: chart.seriesColorsPath,
      suggestion: "Name one theme colour per series, so the chart is part of the document rather than a picture pasted into it.",
      context: { seriesCount: chart.seriesCount },
      ...options.seriesColorFix && options.seriesColorFix.length > 0 && {
        fixes: options.seriesColorFix
      }
    });
  }
  if (!chart.unitStated) {
    findings.push({
      code: QUALITY_CODES.CHART_UNITS,
      severity: "info",
      category: "information-design",
      certainty: "deterministic",
      message: "Nothing on the chart names the unit of its numbers \u2014 the axis title, the value format and the chart title are all silent.",
      path: chart.path,
      suggestion: 'Put the unit where the numbers are: a value-axis title like "Revenue (\u20ACm)", or a format code on the labels.'
    });
  }
  if (chart.annotation && !chart.annotation.stated) {
    findings.push({
      code: QUALITY_CODES.CHART_ANNOTATION,
      severity: "info",
      category: "information-design",
      certainty: "deterministic",
      message: `Nothing in \`${chart.annotation.slot}\`: the chart states no takeaway and cites no source.`,
      path: chart.annotation.path,
      context: { slot: chart.annotation.slot },
      suggestion: "Caption the chart with what it shows and where the numbers came from; a chart nobody has to interpret is worth the line."
    });
  }
  return findings;
}
function overloadedFinding(chart, input) {
  return {
    code: QUALITY_CODES.CHART_OVERLOADED,
    severity: "warning",
    category: "information-design",
    certainty: "deterministic",
    message: input.message,
    path: chart.path,
    suggestion: input.suggestion,
    context: {
      chartType: chart.chartType,
      [input.unit]: input.actual,
      maximum: input.maximum
    },
    evidence: {
      actual: input.actual,
      expected: input.maximum,
      unit: input.unit
    }
  };
}
function record(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? value : void 0;
}
function text(value) {
  return typeof value === "string" ? value : "";
}
function finite(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : void 0;
}
var ANGLE_TYPES = /* @__PURE__ */ new Set(["pie", "variablepie", "funnel", "pyramid"]);
var LENGTH_TYPES = /* @__PURE__ */ new Set([
  "bar",
  "column",
  "waterfall",
  "bullet",
  "columnrange",
  "columnpyramid"
]);
var POSITION_TYPES = /* @__PURE__ */ new Set([
  "line",
  "spline",
  "area",
  "areaspline",
  "arearange",
  "scatter",
  "bubble"
]);
function chartEncodingFor(chartType) {
  const name = chartType.toLowerCase();
  if (ANGLE_TYPES.has(name) || name === "doughnut" || name === "donut") {
    return "angle";
  }
  if (LENGTH_TYPES.has(name) || name.startsWith("bar") || name === "histogram") {
    return "length";
  }
  if (POSITION_TYPES.has(name)) return "position";
  return "other";
}
function normalizeHighchartsChart(options) {
  const config = record(options) ?? {};
  const chart = record(config.chart) ?? {};
  const series = Array.isArray(config.series) ? config.series : [];
  const seriesRecords = series.flatMap((entry) => {
    const found = record(entry);
    return found ? [found] : [];
  });
  const chartType = text(chart.type) || text(seriesRecords[0]?.type) || "line";
  const options3d = record(chart.options3d);
  const categoryCount = Math.max(
    0,
    ...seriesRecords.map(
      (entry) => Array.isArray(entry.data) ? entry.data.length : 0
    ),
    Array.isArray(record(config.xAxis)?.categories) ? (record(config.xAxis)?.categories).length : 0
  );
  const paletteStated = Array.isArray(config.colors) && config.colors.length > 0 || seriesRecords.length > 0 && seriesRecords.every((entry) => typeof entry.color === "string");
  const yAxis = Array.isArray(config.yAxis) ? record(config.yAxis[0]) : record(config.yAxis);
  const labelFormats = [
    ...Object.values(record(config.plotOptions) ?? {}).flatMap((entry) => {
      const labels = record(record(entry)?.dataLabels);
      return [text(labels?.format), text(labels?.valueSuffix)];
    }),
    text(record(yAxis?.labels)?.format),
    text(record(config.tooltip)?.valueSuffix)
  ];
  return {
    chartType,
    encoding: chartEncodingFor(chartType),
    threeD: options3d?.enabled === true,
    seriesCount: seriesRecords.length,
    categoryCount,
    seriesColorsStated: paletteStated,
    ...finite(yAxis?.min) !== void 0 && {
      valueAxisMin: finite(yAxis?.min)
    },
    unitStated: hasUnitMarker(text(record(yAxis?.title)?.text)) || hasUnitMarker(text(record(config.title)?.text)) || hasUnitMarker(text(record(config.subtitle)?.text)) || labelFormats.some((format) => hasUnitMarker(format)),
    annotationStated: text(record(config.caption)?.text).trim() !== "" || text(record(config.subtitle)?.text).trim() !== ""
  };
}
function tableInfoDesignFindings(table, options = {}) {
  const findings = [];
  for (const column of table.columns) {
    const profile = columnNumericProfile(column.values);
    if (!profile.numeric) continue;
    if (column.alignment !== "right") {
      const fixes = options.alignFix?.(column);
      findings.push({
        code: QUALITY_CODES.TABLE_NUMERIC_ALIGN,
        severity: "warning",
        category: "information-design",
        certainty: "deterministic",
        message: `${describeColumn(column)} holds ${profile.counted} numbers and is aligned ${column.alignment} \u2014 digits only line up by place value when they are flush right.`,
        path: column.path,
        suggestion: "Right-align the column, header included, so units sit over units and the reader can compare magnitudes down the column.",
        context: {
          column: column.index,
          alignment: column.alignment,
          ...column.header !== void 0 && { header: column.header }
        },
        evidence: {
          actual: column.alignment,
          expected: "right",
          values: { numbers: profile.counted }
        },
        ...fixes && fixes.length > 0 && { fixes }
      });
    }
    if (profile.decimals.length > 1) {
      findings.push({
        code: QUALITY_CODES.TABLE_MIXED_DECIMALS,
        severity: "warning",
        category: "information-design",
        certainty: "deterministic",
        message: `${describeColumn(column)} is rounded ${profile.decimals.length} different ways (${profile.decimals.join(", ")} decimal places) \u2014 a column of numbers reads as one quantity, and inconsistent precision reads as inconsistent data.`,
        path: column.path,
        suggestion: "Round every cell in the column to the precision the source actually supports, and pad the rest with zeros.",
        context: {
          column: column.index,
          decimalPlaces: profile.decimals,
          ...column.header !== void 0 && { header: column.header }
        },
        evidence: {
          actual: profile.decimals,
          unit: "decimal places"
        }
      });
    }
  }
  if (table.fullGrid) {
    findings.push({
      code: QUALITY_CODES.TABLE_GRID,
      severity: "info",
      category: "information-design",
      certainty: "deterministic",
      message: "Every cell is boxed on all four sides, which spends ink on the container rather than on the numbers and reads as a spreadsheet.",
      path: table.gridPath ?? table.path,
      suggestion: "Keep a rule under the header and, where rows need separating, between rows; drop the vertical rules and the outer box, and let alignment and space do the work."
    });
  }
  const maximumRows = options.maximumRows;
  if (maximumRows !== void 0 && table.rowCount > maximumRows) {
    const surface = options.rowSurface ?? "page";
    findings.push({
      code: QUALITY_CODES.TABLE_ROW_COUNT,
      severity: options.rowSeverity ?? "info",
      category: "information-design",
      certainty: "deterministic",
      message: `${table.rowCount} rows ${surface === "slide" ? "on one slide" : "in one table"} \u2014 past ${maximumRows} the table is a data dump rather than something anybody reads in place.`,
      path: table.path,
      suggestion: surface === "slide" ? "Show the rows that carry the message and move the rest to an appendix or to the document." : "Summarise the rows that carry the argument here and move the full set to an appendix.",
      context: { rows: table.rowCount, maximum: maximumRows },
      evidence: {
        actual: table.rowCount,
        expected: maximumRows,
        unit: "rows"
      }
    });
  }
  return findings;
}
function describeColumn(column) {
  return column.header !== void 0 && column.header.trim() !== "" ? `Column "${column.header.trim()}"` : `Column ${column.index + 1}`;
}
var BLANK_CELL = /^(?:|[-–—]|n\/?a|n\.a\.|tbd|\.|·|–|—)$/i;
var CURRENCY = /[$€£¥₹¢₽₺]|\b(?:usd|eur|gbp|chf|jpy|cad|aud)\b/gi;
var MAGNITUDE_SUFFIX = /(?<=\d)\s*(?:k|m|mn|bn|b|tn|pp|bps|x)\b/i;
function columnNumericProfile(values) {
  const decimals = /* @__PURE__ */ new Set();
  let counted = 0;
  for (const value of values) {
    const trimmed = value.trim();
    if (BLANK_CELL.test(trimmed)) continue;
    const parsed = parseNumericCell(trimmed);
    if (!parsed) return { numeric: false, decimals: [], counted };
    counted += 1;
    decimals.add(parsed.decimals);
  }
  return {
    numeric: counted >= 2,
    decimals: [...decimals].sort((a, b) => a - b),
    counted
  };
}
function parseNumericCell(text2) {
  let body = text2.trim();
  if (body === "") return void 0;
  let sign = 1;
  const bracketed = /^\((.*)\)$/.exec(body);
  if (bracketed) {
    sign = -1;
    body = bracketed[1].trim();
  }
  body = body.replace(CURRENCY, "").replace(/%/g, "").trim();
  body = body.replace(MAGNITUDE_SUFFIX, "").trim();
  body = body.replace(/[\s'’]/g, "");
  const leading = /^([+\-−])(.*)$/.exec(body);
  if (leading) {
    if (leading[1] !== "+") sign = -sign;
    body = leading[2];
  }
  if (body === "" || !/^[\d.,]+$/.test(body)) return void 0;
  const separators = [...body].filter(
    (character) => character === "." || character === ","
  );
  if (separators.length === 0) {
    if (!/^\d+$/.test(body)) return void 0;
    return { value: sign * Number(body), decimals: 0 };
  }
  const lastIndex = Math.max(body.lastIndexOf("."), body.lastIndexOf(","));
  const head = body.slice(0, lastIndex);
  const tail = body.slice(lastIndex + 1);
  if (!/^\d+$/.test(tail) || tail === "") return void 0;
  if (head !== "" && !/^\d{1,3}(?:[.,]\d{3})*$/.test(head)) return void 0;
  const digitsOnlyHead = head.replace(/[.,]/g, "");
  const otherMark = body[lastIndex] === "." ? "," : ".";
  const thousands = tail.length === 3 && !head.includes(otherMark);
  const value = thousands ? Number(`${digitsOnlyHead}${tail}`) : Number(`${digitsOnlyHead === "" ? "0" : digitsOnlyHead}.${tail}`);
  if (!Number.isFinite(value)) return void 0;
  return { value: sign * value, decimals: thousands ? 0 : tail.length };
}
var UNIT_MARKER = /[%$€£¥₹]|\([^)]{1,24}\)|\b(?:bn|mn|pp|bps|kg|km|kwh|mwh?|gwh?|tco2e?|usd|eur|gbp|chf|units?|hours?|days?|weeks?|months?|years?|fte|index|per\s+\w+)\b/i;
function hasUnitMarker(label) {
  return UNIT_MARKER.test(label);
}
var DEFAULT_IMAGE_ASPECT_TOLERANCE = 0.02;
var aspectLabel = (ratio) => `${Math.round(ratio * 100) / 100}:1`;
function imageAspectFinding(image, surface, tolerance = DEFAULT_IMAGE_ASPECT_TOLERANCE) {
  if (!Number.isFinite(image.drawn) || !Number.isFinite(image.natural) || image.natural <= 0)
    return void 0;
  if (Math.abs(image.drawn - image.natural) / image.natural <= tolerance)
    return void 0;
  const sides = surface === "page" ? "width and height" : "w and h";
  return {
    path: image.path,
    message: `The image is drawn at ${aspectLabel(image.drawn)} and the asset is ${aspectLabel(image.natural)}, so it is stretched on the ${surface}.`,
    suggestion: `State one of ${sides} and let the other follow the asset, or crop the asset to the shape you want.`,
    context: { drawn: image.drawn, natural: image.natural },
    evidence: {
      actual: Math.round(image.drawn * 1e3) / 1e3,
      expected: Math.round(image.natural * 1e3) / 1e3,
      values: { source: "asset" }
    },
    ...image.sides && { fixes: [aspectFix(image, image.sides)] }
  };
}
function aspectFix(image, sides) {
  const scale = 10 ** sides.decimals;
  const inward = (value) => Math.max(1 / scale, Math.floor(value * scale) / scale);
  return image.natural > image.drawn ? {
    op: "replace",
    path: sides.height.path,
    value: inward(sides.width.value / image.natural)
  } : {
    op: "replace",
    path: sides.width.path,
    value: inward(sides.height.value * image.natural)
  };
}

// src/type-consistency.ts
var SIZE_TOLERANCE_PT = 0.25;
var near = (a, b) => Math.abs(a - b) <= SIZE_TOLERANCE_PT;
var patchable = (sizes) => sizes.filter((size) => size.sizePath !== void 0 && !size.generated);
function driftingSizes(sizes, roleSizesPt) {
  const byRole = /* @__PURE__ */ new Map();
  for (const size of sizes) {
    if (size.role === void 0) continue;
    byRole.set(size.role, [...byRole.get(size.role) ?? [], size]);
  }
  const drifting = /* @__PURE__ */ new Map();
  for (const [role, members] of byRole) {
    const painted = [...new Set(members.map((m) => m.fontSizePt))].sort(
      (a, b) => a - b
    );
    if (painted.length < 2) continue;
    const expected = roleSizesPt[role];
    if (expected === void 0) continue;
    for (const member of patchable(members))
      if (!near(member.fontSizePt, expected))
        drifting.set(member, { expected, sizes: painted });
  }
  return drifting;
}
function nearestSize(size, scale) {
  let best = scale[0];
  for (const candidate of scale)
    if (Math.abs(candidate - size) < Math.abs(best - size)) best = candidate;
  return best;
}
function offScaleFindings(sizes, scale, themeName, vocabulary, drifting = /* @__PURE__ */ new Map()) {
  if (scale.length === 0) return [];
  const drifted = new Set([...drifting.keys()].map((size) => size.sizePath));
  const offScale = patchable(sizes).filter(
    (size) => !drifted.has(size.sizePath) && !scale.some((step) => near(step, size.fontSizePt))
  );
  const groups = /* @__PURE__ */ new Map();
  for (const size of offScale) {
    const key = `${size.role ?? ""}@${size.fontSizePt}`;
    groups.set(key, [...groups.get(key) ?? [], size]);
  }
  return [...groups.values()].map((members) => {
    const [first] = members;
    const nearest = nearestSize(first.fontSizePt, scale);
    const sizePaths = members.map((member) => member.sizePath);
    const count = members.length === 1 ? "" : ` (${members.length} places, patched together)`;
    const role = first.role ?? `the ${vocabulary.subject} default`;
    return {
      path: sizePaths[0],
      ...sizePaths.length > 1 && { relatedPaths: sizePaths.slice(1) },
      message: `${first.fontSizePt}pt is not a size the ${themeName} theme paints; the nearest on its scale is ${nearest}pt${count}.`,
      suggestion: `Use ${nearest}pt, or drop the size and let "${role}" set it.`,
      context: { role: first.role, scale, paths: sizePaths },
      evidence: {
        actual: first.fontSizePt,
        expected: nearest,
        unit: "pt",
        values: { source: "theme" }
      },
      fixes: sizePaths.map(
        (path) => ({ op: "replace", path, value: nearest })
      )
    };
  });
}
function sizeCountFinding(sizes, maximum, themePath, setBy, vocabulary) {
  const firstPathBySize = /* @__PURE__ */ new Map();
  for (const size of sizes) {
    const rounded = Math.round(size.fontSizePt * 4) / 4;
    if (!firstPathBySize.has(rounded)) firstPathBySize.set(rounded, size.path);
  }
  if (firstPathBySize.size <= maximum) return [];
  const painted = [...firstPathBySize.keys()].sort((a, b) => a - b);
  return [
    {
      path: themePath,
      relatedPaths: painted.map((size) => firstPathBySize.get(size)),
      message: `The ${vocabulary.subject} paints ${painted.length} distinct text sizes (${painted.join(", ")}pt); ${setBy.label} allows ${maximum}.`,
      suggestion: vocabulary.keepTo,
      context: { sizes: painted, maximum },
      evidence: {
        actual: painted.length,
        expected: maximum,
        values: { source: setBy.source }
      }
    }
  ];
}
function roleDriftFindings(sizes, roleSizesPt) {
  const findings = [];
  for (const [size, { expected, sizes: painted }] of driftingSizes(
    sizes,
    roleSizesPt
  )) {
    const keeper = sizes.find(
      (member) => member.role === size.role && near(member.fontSizePt, expected)
    );
    const others = painted.filter((value) => value !== size.fontSizePt);
    const sizePath = size.sizePath;
    findings.push({
      path: sizePath,
      ...keeper && { relatedPaths: [keeper.path] },
      message: `"${size.role}" is painted at ${size.fontSizePt}pt here and at ${others.join("pt, ")}pt elsewhere; the theme sets it at ${expected}pt.`,
      suggestion: `Drop the size so "${size.role}" paints at the theme's ${expected}pt.`,
      context: { role: size.role, sizes: painted },
      evidence: {
        actual: size.fontSizePt,
        expected,
        unit: "pt",
        values: { role: size.role, source: "theme" }
      },
      fixes: [{ op: "replace", path: sizePath, value: expected }]
    });
  }
  return findings;
}

// src/registry.ts
var DuplicateQualityRuleError = class extends Error {
  constructor(ruleId) {
    super(`Quality rule "${ruleId}" is already registered`);
    this.name = "DuplicateQualityRuleError";
  }
};
var QualityRuleRegistry = class {
  entries = /* @__PURE__ */ new Map();
  constructor(rules = []) {
    for (const rule of rules) this.register(rule);
  }
  register(rule) {
    if (this.entries.has(rule.id)) {
      throw new DuplicateQualityRuleError(rule.id);
    }
    this.entries.set(rule.id, rule);
    return this;
  }
  registerPack(pack) {
    const ids = new Set(this.entries.keys());
    for (const rule of pack.rules) {
      if (ids.has(rule.id)) {
        throw new DuplicateQualityRuleError(rule.id);
      }
      ids.add(rule.id);
    }
    for (const rule of pack.rules) this.register(rule);
    return this;
  }
  has(ruleId) {
    return this.entries.has(ruleId);
  }
  get(ruleId) {
    return this.entries.get(ruleId);
  }
  rules(format) {
    const all = [...this.entries.values()];
    if (format === void 0) return all;
    return all.filter(
      (rule) => rule.formats === void 0 || rule.formats.includes(format)
    );
  }
};

// src/placeholder.ts
var SCAFFOLD_MARKER_SYNTAX = "{{\u2026}}";
function unrenderedComponentText(name) {
  return `[Unsupported component type: ${name}]`;
}
function unrenderedComponentName(text2) {
  return /\[Unsupported component type: ([^\]]*\S)\s*\]/.exec(text2)?.[1];
}
function isScaffoldMarker(value) {
  return /^\s*\{\{\s*[^{}\n]*?\S[^{}\n]*?\s*\}\}\s*$/.test(value);
}
var EXCERPT_MAX = 60;
var PATTERNS = [
  {
    id: "scaffold-marker",
    kind: "scaffold-marker",
    expression: /\{\{\s*[^{}\n]*?\S[^{}\n]*?\s*\}\}/
  },
  { id: "lorem-ipsum", kind: "filler", expression: /lorem\s+ipsum/i },
  {
    id: "your-x-here",
    kind: "filler",
    expression: /\byour\b[^.!?\n]{0,40}?\bhere\b/i
  },
  {
    id: "goes-here",
    kind: "filler",
    expression: /\b(?:goes\s+here|go\s+here|(?:text|title|subtitle|content|image|logo|caption|name)\s+here)\b/i
  },
  {
    id: "click-to-add",
    kind: "filler",
    expression: /\bclick\s+to\s+(?:add|edit)\b/i
  },
  {
    id: "bracketed",
    kind: "filler",
    expression: /^\[[^[\]\n]*[A-Za-z][^[\]\n]*\]$/
  },
  {
    id: "debris",
    kind: "filler",
    expression: /^(?:todo|fixme|x{3,}|placeholder|sample\s+text|dummy\s+text|insert\s+text)$/i
  }
];
function excerpt(value) {
  const collapsed = value.replace(/\s+/g, " ").trim();
  return collapsed.length > EXCERPT_MAX ? `${collapsed.slice(0, EXCERPT_MAX - 1)}\u2026` : collapsed;
}
function detectPlaceholder(text2) {
  const trimmed = text2.trim();
  if (trimmed === "") return void 0;
  for (const pattern of PATTERNS) {
    const found = pattern.expression.exec(trimmed);
    if (found) {
      return {
        kind: pattern.kind,
        pattern: pattern.id,
        excerpt: excerpt(found[0])
      };
    }
  }
  return void 0;
}
var PLACEHOLDER_PATTERN_IDS = PATTERNS.map(
  (pattern) => pattern.id
);
function pointerSegment2(value) {
  return value.replace(/~/g, "~0").replace(/\//g, "~1");
}
function collectPlaceholders(document) {
  const found = [];
  const visit = (node, path) => {
    if (typeof node === "string") {
      const match = detectPlaceholder(node);
      if (match) found.push({ path, text: node, match });
      return;
    }
    if (Array.isArray(node)) {
      node.forEach((entry, index) => visit(entry, `${path}/${index}`));
      return;
    }
    if (typeof node !== "object" || node === null) return;
    const record2 = node;
    if (record2.enabled === false) return;
    for (const [key, value] of Object.entries(record2)) {
      visit(value, `${path}/${pointerSegment2(key)}`);
    }
  };
  visit(document, "");
  return found;
}
function placeholderFinding(input) {
  const marker = input.kind === "scaffold-marker";
  return {
    code: marker ? QUALITY_CODES.SCAFFOLD_MARKER : QUALITY_CODES.PLACEHOLDER_TEXT,
    severity: "warning",
    category: "integrity",
    certainty: "deterministic",
    message: marker ? `Unfilled scaffold slot: "${input.excerpt}". Generation refuses a document that still carries a marker.` : `Placeholder text left in the document: "${input.excerpt}".`,
    path: input.path,
    suggestion: marker ? "Patch the slot with real content, or remove the component that holds it." : "Replace it with the real content, or delete the component.",
    context: { kind: input.kind, pattern: input.pattern },
    evidence: { summary: input.excerpt }
  };
}

// src/engine.ts
var QualityProfileError = class extends Error {
  code = "QUALITY_PROFILE_INCOMPATIBLE";
  constructor(message) {
    super(message);
    this.name = "QualityProfileError";
  }
};
var QualityGateError = class extends Error {
  code = "QUALITY_GATE_FAILED";
  analysis;
  constructor(analysis) {
    const blocking = analysis.diagnostics.filter(
      (diagnostic) => diagnostic.blocking
    );
    super(
      `Quality gate failed with ${blocking.length} blocking diagnostic${blocking.length === 1 ? "" : "s"}`
    );
    this.name = "QualityGateError";
    this.analysis = analysis;
  }
};
function errorMessage(error) {
  return error instanceof Error ? error.message : String(error);
}
function emptyCounts() {
  return { error: 0, warning: 0, info: 0 };
}
function countDiagnostics(diagnostics) {
  const counts = emptyCounts();
  for (const diagnostic of diagnostics) counts[diagnostic.severity] += 1;
  return counts;
}
function assertProfileSupports(profile, prepared) {
  if (profile?.formats && !profile.formats.includes(prepared.format)) {
    throw new QualityProfileError(
      `Quality profile "${profile.id}" does not support format "${prepared.format}"`
    );
  }
  if (profile?.rendererTargets) {
    if (!prepared.renderer) {
      throw new QualityProfileError(
        `Quality profile "${profile.id}" declares renderer targets, but the prepared document has no renderer identity`
      );
    }
    if (!profile.rendererTargets.includes(prepared.renderer)) {
      throw new QualityProfileError(
        `Quality profile "${profile.id}" does not support renderer "${prepared.renderer}"`
      );
    }
  }
}
var QualityEngine = class {
  registry;
  constructor(rules = []) {
    this.registry = rules instanceof QualityRuleRegistry ? rules : new QualityRuleRegistry(rules);
  }
  async analyze(prepared, options = {}) {
    assertValidQualityPolicy(options.policy);
    assertValidQualityProfile(options.profile);
    assertProfileSupports(options.profile, prepared);
    return this.runAsync(prepared, options);
  }
  analyzeSync(prepared, options = {}) {
    assertValidQualityPolicy(options.policy);
    assertValidQualityProfile(options.profile);
    assertProfileSupports(options.profile, prepared);
    const state = createAnalysisState();
    for (const rule of this.registry.rules(prepared.format)) {
      const configuration = resolveRuleConfiguration(
        rule,
        options.profile,
        options.policy
      );
      if (!configuration.enabled) continue;
      state.evaluatedRuleIds.push(rule.id);
      let findings;
      try {
        findings = rule.evaluate({
          prepared,
          facts: prepared.facts,
          profile: options.profile,
          policy: options.policy,
          configuration
        });
      } catch (error) {
        handleRuleError(state.ruleErrors, rule.id, error, options.policy);
        continue;
      }
      if (isPromiseLike(findings)) {
        throw new Error(
          `Quality rule "${rule.id}" is asynchronous; use analyze()`
        );
      }
      appendFindings(state, rule, findings, options);
    }
    return finalizeAnalysis(state, options);
  }
  async runAsync(prepared, options) {
    assertProfileSupports(options.profile, prepared);
    const state = createAnalysisState();
    for (const rule of this.registry.rules(prepared.format)) {
      const configuration = resolveRuleConfiguration(
        rule,
        options.profile,
        options.policy
      );
      if (!configuration.enabled) continue;
      state.evaluatedRuleIds.push(rule.id);
      let findings;
      try {
        findings = await rule.evaluate({
          prepared,
          facts: prepared.facts,
          profile: options.profile,
          policy: options.policy,
          configuration
        });
      } catch (error) {
        handleRuleError(state.ruleErrors, rule.id, error, options.policy);
        continue;
      }
      appendFindings(state, rule, findings, options);
    }
    return finalizeAnalysis(state, options);
  }
};
function createAnalysisState() {
  return {
    diagnostics: [],
    evaluatedRuleIds: [],
    ruleErrors: [],
    suppressedCount: 0
  };
}
function isPromiseLike(value) {
  return typeof value === "object" && value !== null && typeof value.then === "function";
}
function handleRuleError(ruleErrors, ruleId, error, policy) {
  if (policy?.onRuleError === "throw") throw error;
  ruleErrors.push({ ruleId, message: errorMessage(error) });
}
function appendFindings(state, rule, findings, options) {
  const configuration = resolveRuleConfiguration(
    rule,
    options.profile,
    options.policy
  );
  for (const finding of findings) {
    const severity = configuration.severityOverride ?? finding.severity ?? configuration.severity;
    const diagnostic = {
      source: "quality",
      ruleId: rule.id,
      code: finding.code ?? rule.code,
      category: finding.category ?? rule.category,
      certainty: finding.certainty ?? rule.defaultCertainty,
      severity,
      message: finding.message,
      path: finding.path,
      blocking: isBlocking(severity, options.policy?.gate),
      ...options.profile && { profileId: options.profile.id },
      ...finding.suggestion !== void 0 && {
        suggestion: finding.suggestion
      },
      ...finding.context !== void 0 && { context: finding.context },
      ...finding.relatedPaths !== void 0 && {
        relatedPaths: finding.relatedPaths
      },
      ...finding.evidence !== void 0 && {
        evidence: finding.evidence
      },
      ...finding.fixes !== void 0 && { fixes: finding.fixes }
    };
    if (isSuppressed(diagnostic, options.policy?.suppressions)) {
      state.suppressedCount += 1;
      continue;
    }
    state.diagnostics.push(diagnostic);
  }
}
function finalizeAnalysis(state, options) {
  const counts = countDiagnostics(state.diagnostics);
  const blocked = state.diagnostics.some((diagnostic) => diagnostic.blocking);
  const maxDiagnostics = options.policy?.maxDiagnostics;
  let kept = state.diagnostics;
  let truncated = false;
  if (maxDiagnostics !== void 0 && Number.isInteger(maxDiagnostics) && maxDiagnostics >= 0 && state.diagnostics.length > maxDiagnostics) {
    const ordered = state.diagnostics.map((diagnostic, index) => ({ diagnostic, index })).sort(
      (a, b) => severityRank(a.diagnostic.severity) - severityRank(b.diagnostic.severity) || a.index - b.index
    );
    const blockingCount = ordered.filter(
      ({ diagnostic }) => diagnostic.blocking
    ).length;
    let budget = Math.max(0, maxDiagnostics - blockingCount);
    kept = ordered.filter(({ diagnostic }) => {
      if (diagnostic.blocking) return true;
      if (budget === 0) return false;
      budget -= 1;
      return true;
    }).map(({ diagnostic }) => diagnostic);
    truncated = kept.length < state.diagnostics.length;
  }
  return {
    diagnostics: kept,
    counts,
    blocked,
    truncated,
    suppressedCount: state.suppressedCount,
    evaluatedRuleIds: state.evaluatedRuleIds,
    ruleErrors: state.ruleErrors,
    ...options.profile && { profileId: options.profile.id }
  };
}
function qualitySeverityCounts(diagnostics) {
  return countDiagnostics(diagnostics);
}
export {
  DEFAULT_IMAGE_ASPECT_TOLERANCE,
  DEFAULT_MAXIMUM_CHART_SERIES,
  DEFAULT_MAXIMUM_PIE_SLICES,
  DuplicateQualityRuleError,
  PLACEHOLDER_PATTERN_IDS,
  QUALITY_CODES,
  QualityEngine,
  QualityGateError,
  QualityPolicyError,
  QualityProfileError,
  QualityRuleRegistry,
  SCAFFOLD_MARKER_SYNTAX,
  SIZE_TOLERANCE_PT,
  assertValidQualityPolicy,
  assertValidQualityProfile,
  chartEncodingFor,
  chartInfoDesignFindings,
  collectColorLiterals,
  collectFontFamilies,
  collectPlaceholders,
  columnNumericProfile,
  configurationLabel,
  configurationSource,
  detectPlaceholder,
  driftingSizes,
  fontCountFinding,
  hasUnitMarker,
  imageAspectFinding,
  isBlocking,
  isScaffoldMarker,
  isSuppressed,
  mergeQualityProfiles,
  nearestPaletteToken,
  normalizeHex,
  normalizeHighchartsChart,
  offPaletteFinding,
  offScaleFindings,
  parseNumericCell,
  placeholderFinding,
  qualitySeverityCounts,
  resolveRuleConfiguration,
  roleDriftFindings,
  severityRank,
  sizeCountFinding,
  tableInfoDesignFindings,
  unrenderedComponentName,
  unrenderedComponentText
};
//# sourceMappingURL=index.js.map